import * as React from "react";
import {
  Control,
  DeepRequired,
  FieldErrorsImpl,
  GlobalError,
  useController
} from "react-hook-form";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";

import { APPOINTMENT_TYPE_SCHEDULE } from "constants/appointment-types";
import { useGetAppointmentTypesQuery } from "services/appointment-types";
import { formatDurationValue } from "utils/common";

import { AppointmentFormValues } from "../appointment-form.component";

type EventTypeFieldProps = {
  control: Control<AppointmentFormValues, object>;
  errors: Partial<FieldErrorsImpl<DeepRequired<AppointmentFormValues>>> & {
    root?: Record<string, GlobalError> & GlobalError;
  };
};

export default function EventTypeField({
  control,
  errors
}: EventTypeFieldProps) {
  const {
    field: { onChange, ...restField }
  } = useController({
    name: "appointment_type",
    control
  });
  const { data: appointmentTypes } = useGetAppointmentTypesQuery({
    is_active: true,
    type: APPOINTMENT_TYPE_SCHEDULE
  });

  if (!appointmentTypes || !appointmentTypes.length) {
    return null;
  }

  return (
    <Box pl="36px">
      <Autocomplete
        disablePortal
        options={appointmentTypes}
        fullWidth
        popupIcon={<ExpandMoreOutlinedIcon />}
        renderOption={(props, { name, default_duration, color }) => {
          return (
            //@ts-ignore
            <Box
              {...props}
              display="flex"
              direction="row"
              gap={2}
              justifyContent="space-between"
            >
              <div>
                {name}
                {default_duration &&
                  ` (${formatDurationValue(default_duration?.slice(0, -3))})`}
              </div>

              {color && (
                <Box
                  sx={{
                    borderRadius: "50%",
                    backgroundColor: color,
                    width: 15,
                    height: 15
                  }}
                />
              )}
            </Box>
          );
        }}
        getOptionLabel={({ name, default_duration }) =>
          `${name} ${
            default_duration
              ? `(${formatDurationValue(default_duration?.slice(0, -3))})`
              : ""
          }`
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        {...restField}
        renderInput={(params) => (
          <TextField
            {...params}
            {...(Boolean(errors?.appointment_type?.message)
              ? {
                  error: true,
                  helperText: errors?.appointment_type?.message
                }
              : {})}
            label="Event Type"
          />
        )}
        onChange={(_, data) => {
          onChange(data);
          restField.onBlur();
        }}
      />
    </Box>
  );
}
